import React, { useState } from "react";
import { Container, Grid, Typography, Box } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Section,
  SectionLabel,
  DP,
  PillButton,
  OutlineButton,
} from "./HomeStyles";
import { useReveal } from "../../hooks/useReveal";
import { keyframes } from "@mui/material";
import { useLanguage } from "../../contexts/LanguageContext";

const checkPop = keyframes`
  0%   { transform: scale(0) rotate(-15deg); opacity: 0; }
  60%  { transform: scale(1.2) rotate(5deg); opacity: 1; }
  100% { transform: scale(1) rotate(0deg); opacity: 1; }
`;
const imageReveal = keyframes`
  from { opacity: 0; transform: scale(1.08); }
  to   { opacity: 1; transform: scale(1); }
`;

const AboutClinic: React.FC = () => {
  const { ref, visible } = useReveal(0.12);
  const [imgHovered, setImgHovered] = useState(false);
  const { t } = useLanguage();
  const ta = t.home.about;

  return (
    <Section id="about" ref={ref} sx={{ backgroundColor: DP.white }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          {/* Image Collage */}
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(-50px)",
              transition: "all 0.9s cubic-bezier(0.22,1,0.36,1) 0.1s",
            }}
          >
            <Box
              sx={{
                position: "relative",
                height: { xs: 360, md: 560 },
                cursor: "pointer",
              }}
              onMouseEnter={() => setImgHovered(true)}
              onMouseLeave={() => setImgHovered(false)}
            >
              {/* Main image */}
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: "18%",
                  bottom: "18%",
                  borderRadius: "28px",
                  overflow: "hidden",
                  boxShadow: imgHovered
                    ? `0 32px 80px ${DP.shadow}, 0 0 0 3px ${DP.bluePale}`
                    : `0 20px 60px ${DP.shadow}`,
                  transition: "box-shadow 0.4s ease",
                  animation: visible ? `${imageReveal} 0.9s ease both` : "none",
                }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1584820927498-cfe5211fd8bf?w=800&auto=format&fit=crop"
                  alt="Veterinary Team"
                  sx={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.6s ease",
                    transform: imgHovered ? "scale(1.05)" : "scale(1)",
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    background: `linear-gradient(135deg, ${DP.blue}22 0%, transparent 60%)`,
                    transition: "opacity 0.4s ease",
                    opacity: imgHovered ? 1 : 0,
                  }}
                />
              </Box>

              {/* Overlay card image */}
              <Box
                sx={{
                  position: "absolute",
                  bottom: 0,
                  right: 0,
                  width: "46%",
                  height: "52%",
                  borderRadius: "22px",
                  overflow: "hidden",
                  border: `4px solid ${DP.white}`,
                  boxShadow: `0 16px 48px rgba(0,0,0,0.18)`,
                  animation: visible
                    ? `${imageReveal} 0.9s ease 0.25s both`
                    : "none",
                  transition: "transform 0.4s cubic-bezier(0.34,1.56,0.64,1)",
                  transform: imgHovered
                    ? "scale(1.04) translateY(-4px)"
                    : "scale(1)",
                }}
              >
                <Box
                  component="img"
                  src="https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600&auto=format&fit=crop"
                  alt="Veterinary Clinic"
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              </Box>

              {/* Experience badge */}
              <Box
                sx={{
                  position: "absolute",
                  top: { xs: 16, md: 24 },
                  right: "17%",
                  background: `linear-gradient(135deg, ${DP.blue}, ${DP.navyMid})`,
                  borderRadius: "18px",
                  px: 2.5,
                  py: 2,
                  textAlign: "center",
                  boxShadow: `0 12px 36px rgba(21,101,192,0.45)`,
                  border: `3px solid ${DP.white}`,
                  transition: "transform 0.4s cubic-bezier(0.34,1.56,0.64,1)",
                  transform: imgHovered
                    ? "scale(1.08) rotate(-2deg)"
                    : "scale(1)",
                  zIndex: 2,
                }}
              >
                <Typography
                  sx={{
                    color: DP.white,
                    fontFamily: '"Cormorant Garamond", Georgia, serif',
                    fontWeight: 700,
                    fontSize: "2.2rem",
                    lineHeight: 1,
                  }}
                >
                  10+
                </Typography>
                <Typography
                  sx={{
                    color: "rgba(255,255,255,0.8)",
                    fontSize: "0.7rem",
                    fontWeight: 500,
                    letterSpacing: "0.06em",
                  }}
                >
                  {ta.badgeLabel}
                </Typography>
              </Box>
            </Box>
          </Grid>

          {/* Text */}
          <Grid
            size={{ xs: 12, md: 6 }}
            sx={{
              opacity: visible ? 1 : 0,
              transform: visible ? "translateX(0)" : "translateX(50px)",
              transition: "all 0.9s cubic-bezier(0.22,1,0.36,1) 0.2s",
            }}
          >
            <SectionLabel>{ta.sectionLabel}</SectionLabel>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: "2rem", md: "2.8rem" },
                fontWeight: 800,
                color: DP.textDark,
                lineHeight: 1.2,
                mb: 2.5,
                letterSpacing: "-0.02em",
              }}
            >
              {ta.heading}{" "}
              <Box component="span" sx={{ color: DP.blue }}>
                {ta.headingHighlight}
              </Box>{" "}
              {ta.headingEnd}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: "1.02rem",
                color: DP.textMid,
                lineHeight: 1.85,
                mb: 4,
              }}
            >
              {ta.body}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1.5,
                mb: 4,
              }}
            >
              {ta.highlights.map((item, i) => (
                <Box
                  key={i}
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    opacity: visible ? 1 : 0,
                    transform: visible ? "translateX(0)" : "translateX(16px)",
                    transition: `all 0.5s cubic-bezier(0.22,1,0.36,1) ${0.4 + i * 0.055}s`,
                  }}
                >
                  <CheckCircleIcon
                    sx={{
                      color: DP.blue,
                      fontSize: "1rem",
                      mt: "3px",
                      flexShrink: 0,
                      animation: visible
                        ? `${checkPop} 0.5s cubic-bezier(0.34,1.56,0.64,1) both`
                        : "none",
                      animationDelay: `${0.45 + i * 0.06}s`,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "0.85rem",
                      color: DP.textMid,
                      fontWeight: 500,
                      lineHeight: 1.5,
                    }}
                  >
                    {item}
                  </Typography>
                </Box>
              ))}
            </Box>

            <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2, flexWrap: "wrap" }}>
              <PillButton
                variant="contained"
                sx={{
                  width: { xs: "100%", sm: "auto" },
                  backgroundColor: DP.blue,
                  color: DP.white,
                  position: "relative",
                  overflow: "hidden",
                  "&::before": {
                    content: '""',
                    position: "absolute",
                    top: 0,
                    left: "-100%",
                    width: "100%",
                    height: "100%",
                    background:
                      "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                    transition: "left 0.5s ease",
                  },
                  "&:hover::before": { left: "100%" },
                  "&:hover": { backgroundColor: DP.navy },
                  transition: "all 0.3s cubic-bezier(0.34,1.56,0.64,1)",
                }}
                onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })}
              >
                {ta.ctaExplore}
              </PillButton>
              <OutlineButton
                sx={{ width: { xs: "100%", sm: "auto" } }}
                onClick={() =>
                  document
                    .getElementById("services")
                    ?.scrollIntoView({ behavior: "smooth" })
                }
              >
                {ta.ctaServices}
              </OutlineButton>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Section>
  );
};

export default AboutClinic;
